import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  OPP_GROUPS,
  OPPS,
  OPP_GROUP_TYPES,
  OPP_STATUS,
  STAGE_MAP,
  oppGroupType,
  dateFromDaysAgo,
  type Opp,
  type OppGroup,
  type OppStatus,
} from './crmData'
import { Avatar, Card, CloseButton, ConfirmDialog, CrmToaster, IconTrash, Kpi, Overlay, Pill, SearchInput, SegTabs, StageBadge, StatusBadge, crmToast } from './crmUi'
import { Metric } from './Metric'
import { Button } from '../shared/Button'
import { useCrm } from './useCrm'
import { ImportButton } from './ImportButton'
import { getOppGroups, getOppGroup, createOppGroup, convertOpp, updateOpp, getEventRoi, updateOppGroup, archiveOppGroup, archiveOpp, type EventRoi } from './crmApi'
import { useAuth } from '../../contexts/AuthContext'

const round = (n: number) => Math.round(n)
const rateColor = (r: number) => (r >= 40 ? '#16a34a' : r >= 20 ? '#d97706' : '#dc2626')
const ff = (p: Promise<unknown>) => {
  p.catch(() => {})
}
const gbp = (n: number) => `£${Math.round(n).toLocaleString()}`

/**
 * Phase 2.4 — what a show ACTUALLY produced.
 *
 * The old conversion rate counted `opp.status === 'converted'`. The lead import — the way
 * show leads actually get in — creates the opportunity with status 'new' and links the deal
 * it just made. Nobody ever touches the status again. So a show could produce 200 leads,
 * 40 trials and 3 paying customers, and this page would proudly report 0%.
 *
 * Outcomes now come from the linked deal's real stage: captured → in the CRM → demo →
 * trial → paying customer, with the MRR those customers pay. Cost is optional; where it's
 * been entered we show real return, and where it hasn't we say we can't, rather than
 * inventing a denominator.
 */
const EMPTY_ROI: EventRoi[] = []

/** The one number a human owns on this page. Blank until someone enters it — never guessed. */
const CostCell: React.FC<{ event: EventRoi; onSaved: () => void }> = ({ event, onSaved }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(event.cost != null ? String(event.cost) : '')

  const save = () => {
    const n = val.trim() === '' ? null : Number(val)
    if (n != null && (!Number.isFinite(n) || n < 0)) { crmToast('Enter a valid cost', 'error'); return }
    setEditing(false)
    updateOppGroup(event.groupId, { cost: n })
      .then(() => { crmToast(n == null ? 'Cost cleared' : `Cost set to ${gbp(n)}`, 'success'); onSaved() })
      .catch(() => crmToast('Could not save the cost', 'error'))
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        placeholder="0"
        className="w-24 px-2 py-1 rounded border border-border bg-surface text-right text-sm outline-none focus:border-brand-500"
      />
    )
  }
  return (
    <button onClick={() => setEditing(true)} className="text-sm hover:underline" title="Click to set what this event cost">
      {event.cost != null ? <span className="text-txt-secondary">{gbp(event.cost)}</span> : <span className="text-txt-tertiary">+ add</span>}
    </button>
  )
}

export const CrmOpportunities: React.FC = () => {
  const navigate = useNavigate()
  const { data: groups, reload: reloadGroups } = useCrm<OppGroup[]>(getOppGroups, OPP_GROUPS)
  const {
    data: opps,
    setData: setOpps,
    reload: reloadOpps,
  } = useCrm<Opp[]>(async () => {
    const gs = await getOppGroups()
    const lists = await Promise.all(gs.map((g) => getOppGroup(g.id).then((r: { opps: Opp[] }) => r.opps)))
    return lists.flat()
  }, OPPS)

  const { currentAgent } = useAuth()
  const isAdmin = currentAgent?.role === 'admin'
  const [openGroup, setOpenGroup] = useState<OppGroup | null>(null)
  // Stage filter inside the open event — chips over the contact list ('archived' is its own bucket).
  const [stageFilter, setStageFilter] = useState<string | null>(null)
  // Edit event/show details (name, type, date, location, notes).
  const [editGroup, setEditGroup] = useState<null | { name: string; type: string; date: string; location: string; notes: string }>(null)
  const saveGroupEdit = async () => {
    if (!openGroup || !editGroup) return
    if (!editGroup.name.trim()) { crmToast('The event needs a name', 'error'); return }
    try {
      await updateOppGroup(openGroup.id, {
        name: editGroup.name.trim(),
        type: editGroup.type,
        date: editGroup.date || undefined,
        location: editGroup.location.trim(),
        notes: editGroup.notes.trim(),
      })
      crmToast('Event details saved', 'success')
      setOpenGroup({ ...openGroup, name: editGroup.name.trim(), type: editGroup.type, date: editGroup.date || openGroup.date, location: editGroup.location.trim(), notes: editGroup.notes.trim() })
      setEditGroup(null)
      reloadGroups()
      reloadRoi()
    } catch {
      crmToast('Could not save the event details', 'error')
    }
  }
  const [showNewGroup, setShowNewGroup] = useState(false)
  const [view, setView] = useState<'cards' | 'table'>('cards')
  // Page search — filters the event cards/table by name, location or type. Matching also
  // reaches into each event's CAPTURED CONTACTS, so searching a person/company finds the show
  // they were captured at.
  const [q, setQ] = useState('')
  // Contact search inside the open event overlay.
  const [oppQ, setOppQ] = useState('')
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; choices?: { key: string; label: string; hint?: string }[]; onConfirm: (k?: string) => Promise<void> }>(null)

  // Real outcomes, from the linked deals.
  const { data: roi, reload: reloadRoi } = useCrm<EventRoi[]>(getEventRoi, EMPTY_ROI)
  const roiOf = (groupId: string): EventRoi | undefined => roi.find((r) => r.groupId === groupId)

  const oppsOf = (groupId: string) => opps.filter((o) => o.groupId === groupId)
  /** Capture → paying customer. The number that mattered all along. */
  const rate = (group: OppGroup) => roiOf(group.id)?.captureToCustomerPct ?? 0

  const totals = roi.reduce(
    (a, r) => ({
      captured: a.captured + r.captured,
      inCrm: a.inCrm + r.inCrm,
      trials: a.trials + r.trials,
      customers: a.customers + r.customers,
      mrr: a.mrr + r.mrr,
      notFollowedUp: a.notFollowedUp + r.notFollowedUp,
    }),
    { captured: 0, inCrm: 0, trials: 0, customers: 0, mrr: 0, notFollowedUp: 0 },
  )
  const overallRate = totals.captured ? round((totals.customers / totals.captured) * 100) : 0

  const groupMatches = (g: OppGroup) => {
    const needle = q.trim().toLowerCase()
    if (!needle) return true
    if (`${g.name} ${g.location || ''} ${oppGroupType(g.type).label}`.toLowerCase().includes(needle)) return true
    return oppsOf(g.id).some((o) => `${o.name} ${o.company} ${o.email}`.toLowerCase().includes(needle))
  }
  const rankedGroups = [...groups].sort((a, b) => rate(b) - rate(a)).filter(groupMatches)

  const setStatus = (id: string, status: OppStatus) =>
    setOpps((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)))

  const refreshAfterImport = () => {
    reloadOpps()
    reloadGroups()
  }

  // Leads a show produced = captured contacts that became a deal.
  const leadsFromGroup = (groupId: string) => oppsOf(groupId).filter((o) => o.convertedDealId).length

  const doArchiveGroup = (group: OppGroup) => {
    const leadCount = leadsFromGroup(group.id)
    setConfirm({
      title: `Archive “${group.name}”?`,
      message: <>The show and its captured contacts will be hidden from Shows/Events and from event ROI. This is recoverable.</>,
      choices: leadCount > 0
        ? [
            { key: 'show', label: 'Archive the show only', hint: `Keep the ${leadCount} lead${leadCount === 1 ? '' : 's'} it produced live in the pipeline` },
            { key: 'withLeads', label: `Archive the show and its ${leadCount} lead${leadCount === 1 ? '' : 's'}`, hint: 'Also hides the leads this show produced' },
          ]
        : undefined,
      onConfirm: async (k) => {
        const r = await archiveOppGroup(group.id, k === 'withLeads')
        crmToast(r.leadsArchived > 0 ? `Show archived · ${r.leadsArchived} lead${r.leadsArchived === 1 ? '' : 's'} archived too` : 'Show archived', 'success')
        setOpenGroup(null)
        reloadGroups(); reloadOpps(); reloadRoi()
      },
    })
  }

  const doArchiveOpp = (o: Opp) => {
    setConfirm({
      title: `Remove “${o.name}” from this show?`,
      message: <>The captured contact will be archived. The lead it may have produced is not affected.</>,
      onConfirm: async () => {
        await archiveOpp(o.id)
        crmToast('Contact removed', 'success')
        reloadOpps(); reloadRoi()
      },
    })
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      {/* Pinned header: title, actions, KPIs and the Card/Table view toggle. */}
      <div className="px-6 pt-6 pb-3 bg-surface border-b border-border shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-txt-primary">Shows/Events</h1>
            <p className="text-sm text-txt-secondary">
              Group show &amp; event contacts, then upload them — every contact flows into Leads as a cold lead
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <SearchInput value={q} onChange={setQ} placeholder="Search events, companies, people…" className="w-64" />
            <SegTabs
              tabs={[{ key: 'cards', label: 'Cards' }, { key: 'table', label: 'Table' }]}
              value={view}
              onChange={(v) => setView(v as 'cards' | 'table')}
            />
            <Button size="sm" onClick={() => setShowNewGroup(true)}>+ New show / event</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
          <Kpi label="Shows / events" value={groups.length} tone="blue" />
          <Metric id="event_captured" value={totals.captured} />
          <Kpi label="Started a trial" value={totals.trials} tone="blue" />
          <Kpi label="Paying customers" value={totals.customers} tone="green" />
          <Kpi label="MRR from events" value={`${gbp(totals.mrr)}/mo`} tone="green" />
        </div>
        {totals.notFollowedUp > 0 && (
          <p className="text-[12px] px-3 py-2 rounded-lg mt-3" style={{ background: '#FEF3C7', color: '#92400E' }}>
            <span className="font-bold">{totals.notFollowedUp} captured contact(s) never made it into the CRM.</span>{' '}
            They were collected at an event and no lead was ever created — the leak is on our side, not the show's.
          </p>
        )}
      </div>

      {/* Body — only this scrolls. */}
      <div className="flex-1 min-h-0 overflow-auto p-6">
        {view === 'cards' && (<>
        {/* Section heading */}
        <h2 className="text-sm font-semibold text-txt-primary mb-3">
          Shows &amp; events <span className="text-txt-tertiary">· ranked by capture → paying customer</span>
        </h2>

        {/* Group cards */}
        {rankedGroups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
            <p className="text-sm text-txt-secondary">No shows or events yet.</p>
            <Button size="sm" className="mt-3" onClick={() => setShowNewGroup(true)}>
              + Create your first show / event
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
            {rankedGroups.map((group) => {
              const type = oppGroupType(group.type)
              const e = roiOf(group.id)
              const list = oppsOf(group.id)
              const r = rate(group)
              return (
                <button
                  key={group.id}
                  onClick={() => navigate(`/crm/shows/${group.id}`)} // FULL SCREEN, not the old pop-up (Lee, 17 Sep 2026)
                  className="relative text-left rounded-xl bg-surface border border-border p-4 hover:bg-surface-hover hover:border-brand-300 transition-colors flex flex-col gap-4 h-full"
                >
                  <span className="absolute top-3 right-4 text-[11px] font-semibold text-brand-600">Click to see more →</span>

                  <div className="flex items-center gap-3 pr-24">
                    <span
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{ background: type.colour + '22', color: type.colour }}
                    >
                      {type.icon}
                    </span>
                    <div className="min-w-0">
                      {/* Show name → its own SCREEN (Lee, 17 Sep 2026), not a pop-up. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/crm/shows/${group.id}`) }}
                        className="font-semibold text-txt-primary truncate hover:text-brand-600 hover:underline text-left block max-w-full"
                        title="Open this show's screen"
                      >
                        {group.name}
                      </button>
                      <p className="text-xs text-txt-tertiary truncate">
                        {type.label} · {group.location}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-txt-primary leading-tight">{e?.captured ?? list.length}</p>
                      <p className="text-[11px] text-txt-tertiary mt-0.5">captured</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-txt-primary leading-tight">{e?.trials ?? 0}</p>
                      <p className="text-[11px] text-txt-tertiary mt-0.5">trials</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-success leading-tight">{e?.customers ?? 0}</p>
                      <p className="text-[11px] text-txt-tertiary mt-0.5">customers</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold leading-tight" style={{ color: rateColor(r) }}>{r}%</p>
                      <p className="text-[11px] text-txt-tertiary mt-0.5">→ paying</p>
                    </div>
                  </div>

                  <div className="mt-auto space-y-2.5">
                    <div className="h-1.5 rounded bg-surface-tertiary overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${r}%`, background: rateColor(r) }} />
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-xs text-txt-tertiary">{dateFromDaysAgo(group.dateDays)}</p>
                      <p className="text-xs">
                        {e && e.mrr > 0 && <span className="text-success font-semibold">{gbp(e.mrr)}/mo</span>}
                        {e && e.roi != null && <span className="text-txt-secondary"> · {e.roi}× return</span>}
                        {e && e.cost != null && e.roi == null && <span className="text-txt-tertiary"> · no customers yet</span>}
                        {e && e.cost == null && <span className="text-txt-tertiary"> · no cost entered</span>}
                      </p>
                    </div>
                    {e && e.notFollowedUp > 0 && (
                      <p className="text-[11px]" style={{ color: '#B45309' }}>{e.notFollowedUp} never followed up</p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
        </>)}

        {/* Table view — what each show actually produced, with sticky header. */}
        {view === 'table' && (
          <Card
            title="What each show actually produced"
            subtitle="Outcomes follow the linked lead's real stage. Enter a cost to see return; without one we show the funnel, not a made-up ROI."
          >
            {roi.length === 0 ? (
              <p className="text-sm text-txt-tertiary py-6 text-center">No shows or events yet.</p>
            ) : (<>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface z-10">
                  <tr className="text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                    <th className="text-left font-semibold py-2">Show / event</th>
                    <th className="text-right font-semibold">Captured</th>
                    <th className="text-right font-semibold">In CRM</th>
                    <th className="text-right font-semibold">Demos</th>
                    <th className="text-right font-semibold">Trials</th>
                    <th className="text-right font-semibold">Customers</th>
                    <th className="text-right font-semibold">MRR</th>
                    <th className="text-right font-semibold">Cost</th>
                    <th className="text-right font-semibold">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {[...roi].sort((a, b) => b.customers - a.customers || b.trials - a.trials).filter((e) => { const g = groups.find((x) => x.id === e.groupId); return !g || groupMatches(g) }).map((e) => (
                    <tr key={e.groupId} className="border-b border-border last:border-0">
                      <td className="py-2.5 text-txt-primary font-medium">
                        {e.name}
                        {e.medianDaysToCustomer != null && (
                          <span className="text-[11px] text-txt-tertiary"> · {e.medianDaysToCustomer}d to first payment</span>
                        )}
                      </td>
                      <td className="text-right text-txt-secondary">{e.captured}</td>
                      <td className="text-right text-txt-secondary">
                        {e.inCrm}
                        {e.notFollowedUp > 0 && <span style={{ color: '#B45309' }}> (−{e.notFollowedUp})</span>}
                      </td>
                      <td className="text-right text-txt-secondary">{e.demos}</td>
                      <td className="text-right text-txt-secondary">{e.trials}</td>
                      <td className="text-right font-semibold" style={{ color: e.customers ? '#16a34a' : undefined }}>{e.customers}</td>
                      <td className="text-right text-txt-secondary">{e.mrr ? `${gbp(e.mrr)}/mo` : '—'}</td>
                      <td className="text-right">
                        <CostCell event={e} onSaved={reloadRoi} />
                      </td>
                      <td className="text-right font-semibold" style={{ color: e.roi != null && e.roi >= 1 ? '#16a34a' : e.roi != null ? '#dc2626' : undefined }}>
                        {e.roi != null ? `${e.roi}×` : <span className="text-txt-tertiary font-normal">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-txt-tertiary mt-3">
              Return = annualised MRR from the customers this show produced ÷ what it cost. It only appears once you
              enter a cost — we don't hold one, and a return without a denominator would be a guess.
            </p>
            </>)}
          </Card>
        )}
      </div>

      {/* New group modal */}
      {showNewGroup && (
        <NewGroupModal
          onClose={() => setShowNewGroup(false)}
          onCreated={(g) => {
            setShowNewGroup(false)
            reloadGroups()
            crmToast(`Show / event "${g.name}" created`, 'success')
            navigate(`/crm/shows/${g.id}`)
          }}
        />
      )}

      {/* Group modal */}
      {openGroup && (
        <Overlay onClose={() => setOpenGroup(null)} width={760}>
          {(() => {
            const type = oppGroupType(openGroup.type)
            const all = oppsOf(openGroup.id)
            // Stage buckets across every captured contact — the filter chips.
            const bucketOf = (o: Opp) => (o.dealArchived ? 'archived' : o.dealStage || 'not_in_crm')
            const buckets: Record<string, number> = {}
            all.forEach((o) => { const b = bucketOf(o); buckets[b] = (buckets[b] || 0) + 1 })
            const oppNeedle = oppQ.trim().toLowerCase()
            const list = all
              .filter((o) => !stageFilter || bucketOf(o) === stageFilter)
              .filter((o) => !oppNeedle || `${o.name} ${o.company} ${o.email} ${o.title}`.toLowerCase().includes(oppNeedle))
            const bucketLabel = (k: string) => (k === 'archived' ? 'Archived' : k === 'not_in_crm' ? 'Not in CRM' : STAGE_MAP[k]?.label || k.replace(/_/g, ' '))
            const converted = all.filter((o) => o.status === 'converted').length
            const r = rate(openGroup)
            return (
              <>
                <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{ background: type.colour + '22', color: type.colour }}
                    >
                      {type.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-txt-primary truncate">{openGroup.name}</p>
                      <p className="text-xs text-txt-tertiary truncate">
                        {type.label} · {openGroup.location} · {dateFromDaysAgo(openGroup.dateDays)}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Pill tone="gray">{list.length} leads</Pill>
                        <Pill tone="green">{converted} converted</Pill>
                        <Pill tone="blue">{r}% conversion</Pill>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditGroup({ name: openGroup.name, type: openGroup.type, date: openGroup.date || '', location: openGroup.location || '', notes: openGroup.notes || '' })}
                      title="Edit the event's name, type, date, location and notes"
                      className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-border text-txt-primary hover:bg-surface-hover"
                    >
                      ✎ Edit
                    </button>
                    <ImportButton groupId={openGroup.id} onImported={refreshAfterImport} />
                    {isAdmin && (
                      <button
                        onClick={() => doArchiveGroup(openGroup)}
                        title="Archive this show / event"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg text-red-600 border border-border hover:bg-red-50 hover:border-red-300"
                      >
                        <IconTrash className="w-3.5 h-3.5" /> Archive
                      </button>
                    )}
                    <CloseButton onClick={() => setOpenGroup(null)} />
                  </div>
                </div>

                <div className="px-5 py-4 overflow-auto">
                  {editGroup && (
                    <div className="mb-4 rounded-lg border border-brand-300 bg-brand-50/30 p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-[11px] font-semibold text-txt-tertiary">
                          Name
                          <input value={editGroup.name} onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })} className="mt-0.5 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400" />
                        </label>
                        <label className="text-[11px] font-semibold text-txt-tertiary">
                          Type
                          <select value={editGroup.type} onChange={(e) => setEditGroup({ ...editGroup, type: e.target.value })} className="mt-0.5 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400">
                            {OPP_GROUP_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                        </label>
                        <label className="text-[11px] font-semibold text-txt-tertiary">
                          Date
                          <input type="date" value={editGroup.date} onChange={(e) => setEditGroup({ ...editGroup, date: e.target.value })} className="mt-0.5 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400" />
                        </label>
                        <label className="text-[11px] font-semibold text-txt-tertiary">
                          Location
                          <input value={editGroup.location} onChange={(e) => setEditGroup({ ...editGroup, location: e.target.value })} className="mt-0.5 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400" />
                        </label>
                      </div>
                      <label className="text-[11px] font-semibold text-txt-tertiary block">
                        Notes
                        <textarea value={editGroup.notes} onChange={(e) => setEditGroup({ ...editGroup, notes: e.target.value })} rows={2} className="mt-0.5 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400" />
                      </label>
                      <div className="flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setEditGroup(null)}>Cancel</Button>
                        <Button size="sm" onClick={saveGroupEdit}>Save details</Button>
                      </div>
                    </div>
                  )}
                  {!editGroup && openGroup.notes && (
                    <div className="mb-4 rounded-lg bg-surface-secondary border border-border p-3 text-sm text-txt-secondary">
                      {openGroup.notes}
                    </div>
                  )}

                  {/* Stage filter + contact search — where every captured contact stands TODAY. */}
                  {all.length > 0 && (
                    <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                      <SearchInput value={oppQ} onChange={setOppQ} placeholder="Search this event's contacts…" className="w-56 mr-1" />
                      <button
                        onClick={() => setStageFilter(null)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${!stageFilter ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface text-txt-secondary border-border hover:border-brand-400'}`}
                      >
                        All ({all.length})
                      </button>
                      {Object.entries(buckets).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                        <button
                          key={k}
                          onClick={() => setStageFilter(stageFilter === k ? null : k)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${stageFilter === k ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface text-txt-secondary border-border hover:border-brand-400'}`}
                        >
                          {bucketLabel(k)} ({n})
                        </button>
                      ))}
                    </div>
                  )}

                  {list.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-sm text-txt-tertiary">{all.length ? (oppNeedle ? 'No contacts match this search.' : 'No contacts in this stage.') : 'No contacts yet.'}</p>
                      <p className="text-xs text-txt-tertiary mt-1">
                        Upload a CSV or Excel file — every contact becomes a cold lead on the Board.
                      </p>
                      <div className="mt-4 inline-flex">
                        <ImportButton groupId={openGroup.id} onImported={refreshAfterImport} label="Upload contacts" />
                      </div>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                          <th className="py-2 pr-3 font-medium">Name</th>
                          <th className="py-2 pr-3 font-medium">Company</th>
                          <th className="py-2 pr-3 font-medium">Email</th>
                          <th className="py-2 pr-3 font-medium">Stage</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 font-medium text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((o) => {
                          const st = OPP_STATUS[o.status]
                          const isLead = !!o.convertedDealId
                          return (
                            <tr key={o.id} className="border-b border-border last:border-0 align-middle">
                              <td className="py-2.5 pr-3">
                                <div className="flex items-center gap-2">
                                  {/* Logo/uploaded-image aware — initial placeholder when neither exists (Lee, 7 Sep). */}
                                  <Avatar name={o.name} logoUrl={o.avatarUrl || undefined} />
                                  <div className="min-w-0">
                                    <p className="font-medium text-txt-primary truncate">{o.name}</p>
                                    <p className="text-xs text-txt-tertiary truncate">{o.title}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2.5 pr-3 text-txt-secondary">{o.company}</td>
                              <td className="py-2.5 pr-3 text-brand-600">{o.email}</td>
                              <td className="py-2.5 pr-3">
                                {o.dealArchived ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-surface-tertiary text-txt-tertiary border border-border">Archived</span>
                                ) : o.dealStage && STAGE_MAP[o.dealStage] ? (
                                  <StageBadge stageKey={o.dealStage} />
                                ) : o.dealStage ? (
                                  <span className="text-xs text-txt-secondary">{o.dealStage.replace(/_/g, ' ')}</span>
                                ) : (
                                  <span className="text-xs text-txt-tertiary">—</span>
                                )}
                              </td>
                              <td className="py-2.5 pr-3">
                                <StatusBadge label={st.label} colour={st.colour} bg={st.bg} />
                              </td>
                              <td className="py-2.5 text-right">
                                <div className="inline-flex items-center gap-1.5 justify-end">
                                {o.status === 'converted' ? (
                                  <Button size="sm" variant="secondary" onClick={() => crmToast('Opening deal for ' + o.name)}>
                                    View deal →
                                  </Button>
                                ) : isLead ? (
                                  <Pill tone="blue" className="!px-2 !py-1">
                                    ✓ In Leads (cold)
                                  </Pill>
                                ) : o.status === 'dead' ? (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                      setStatus(o.id, 'new')
                                      ff(updateOpp(o.id, { status: 'new' }))
                                      crmToast(`${o.name} reopened`)
                                    }}
                                  >
                                    Reopen
                                  </Button>
                                ) : (
                                  <div className="inline-flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setStatus(o.id, 'converted')
                                        ff(convertOpp(o.id))
                                        crmToast(`${o.name} converted to a deal`, 'success')
                                      }}
                                    >
                                      ✓ Convert to deal
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setStatus(o.id, 'dead')
                                        ff(updateOpp(o.id, { status: 'dead' }))
                                        crmToast('Marked as not a fit')
                                      }}
                                    >
                                      ✕
                                    </Button>
                                  </div>
                                )}
                                {isAdmin && (
                                  <button onClick={() => doArchiveOpp(o)} title="Remove contact from show" className="text-txt-tertiary hover:text-red-600"><IconTrash className="w-4 h-4" /></button>
                                )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )
          })()}
        </Overlay>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          choices={confirm.choices}
          confirmLabel="Archive"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

/* ─── New group modal ─────────────────────────────────────────────────────── */
const NewGroupModal: React.FC<{ onClose: () => void; onCreated: (g: OppGroup) => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('')
  const [type, setType] = useState('trade_show')
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!name.trim()) {
      crmToast('Give the group a name', 'error')
      return
    }
    setSaving(true)
    try {
      const g = await createOppGroup({ name: name.trim(), type, location: location.trim() || undefined, notes: notes.trim() || undefined, dateDays: 0 })
      onCreated(g as OppGroup)
    } catch {
      crmToast('Could not create group', 'error')
      setSaving(false)
    }
  }

  const field = 'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400'
  return (
    <Overlay onClose={onClose} width={480}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
        <p className="font-semibold text-txt-primary">New show / event</p>
        <CloseButton onClick={onClose} />
      </div>
      <div className="px-5 py-4 space-y-3">
        <div>
          <label className="text-xs font-medium text-txt-secondary">Show / event name</label>
          <input className={field + ' mt-1'} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ISE 2026 — Barcelona" autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-txt-secondary">Type</label>
            <select className={field + ' mt-1'} value={type} onChange={(e) => setType(e.target.value)}>
              {OPP_GROUP_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.icon} {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-txt-secondary">Location</label>
            <input className={field + ' mt-1'} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City / venue" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-txt-secondary">Notes (optional)</label>
          <textarea className={field + ' mt-1 resize-none'} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Context for this batch of contacts" />
        </div>
      </div>
      <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" loading={saving} onClick={submit}>
          Create show / event
        </Button>
      </div>
    </Overlay>
  )
}
